import { useEffect, useState, useCallback } from 'react';
import { signalApi } from '../lib/api';

const QUEUE_KEY = 'gd-offline-queue';

function readQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; }
}
function writeQueue(q) { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); }

/**
 * useOfflineQueue — local queue of signalements when offline.
 * Auto-syncs to backend (POST /api/signalements) when connectivity returns.
 */
export function useOfflineQueue() {
  const [queue, setQueue] = useState(readQueue());
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);

  const enqueue = useCallback((item) => {
    setQueue((q) => {
      const next = [...q, { ...item, _enqueuedAt: new Date().toISOString() }];
      writeQueue(next);
      return next;
    });
  }, []);

  const sync = useCallback(async () => {
    const current = readQueue();
    if (!current.length) return { ok: 0, ko: 0 };
    setSyncing(true);
    let ok = 0;
    let ko = 0;
    const remaining = [];
    for (const item of current) {
      try {
        await signalApi.create(item);
        ok += 1;
      } catch {
        ko += 1;
        remaining.push(item);
      }
    }
    writeQueue(remaining);
    setQueue(remaining);
    setSyncing(false);
    return { ok, ko };
  }, []);

  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      if (readQueue().length) sync();
    };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [sync]);

  return { queue, queueSize: queue.length, isOnline, syncing, enqueue, sync };
}
